import type {
  AutoClaimedTaskDto,
  DailyTaskDto,
  DailyTaskRewardDto,
  DailyTasksBalancesDto,
  DailyTasksPayloadDto,
} from "../api/dto/daily_tasks.dto.js";
import {
  getDailyTasksConditional,
  claimDailyTask,
  claimStarChest,
  getDailyTasks,
} from "../api/requests/daily_tasks.requests.js";
import { CumulativeBoosterService } from "../cumulative_boosters/cumulative_booster_service.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import { infiniteHeartsUntilMs, serverUntilMs } from "../api/player_sync.js";
import { NetworkManager } from "../network_manager.js";
import { isNetworkishError } from "../utils/network_errors.js";
import { PersistentCache } from "../offline/persistent_cache.js";
import { EventPayloadListeners, eventPayloadKey, type EventPayloadListener } from "../api/event_payload_key.js";
import { ServerClock } from "../offline/server_clock.js";
import { OfflineJournal } from "../offline/offline_journal.js";
import { applyLocalEventUnlock } from "../offline/local_event_unlock.js";
import { shouldGoLocal } from "../offline/offline_policy.js";
import { OfflineEconomy } from "../offline/offline_economy.js";
import type { LocalMetricEvent } from "../offline/local_metrics.js";
import { DailyTasksAnalytics } from "./daily_tasks_analytics.js";

const CACHE_TTL_MS = 30_000;
const EXPIRED_RETRY_MS = 15_000;
const PERSIST_KEY = "daily_tasks_v1";

const MAX_MODE_METRICS: ReadonlySet<string> = new Set( [ "level_streak" ] );
const SERVER_ONLY_METRICS: ReadonlySet<string> = new Set( [ "login_days", "make_purchases" ] );

interface PersistedDailyTasks
{
  payload: DailyTasksPayloadDto;
  fetched_at: number;
  etag?: string | null;
}

/**
 * Client-side facade over the server's daily tasks. Progress is tracked
 * entirely by the server (hooks inside level/purchase/booster/ad endpoints),
 * so this service only fetches state and claims rewards.
 */
class DailyTasksServiceImpl
{
  private payload: DailyTasksPayloadDto | null = null;
  private etag: string | null = null;
  private fetched_at = 0;
  private pending_fetch: Promise<DailyTasksPayloadDto | null> | null = null;
  private readonly listeners = new EventPayloadListeners<DailyTasksPayloadDto>();
  private pending_auto_claimed: AutoClaimedTaskDto[] = [];

  constructor()
  {
    const stored = PersistentCache.read<PersistedDailyTasks>( PERSIST_KEY );
    if ( stored?.payload && typeof stored.payload === "object" && Array.isArray( stored.payload.tasks ) )
    {
      this.payload = stored.payload;
      this.etag = typeof stored.etag === "string" ? stored.etag : null;
      this.fetched_at = 0;
    }
  }

  public getCached(): DailyTasksPayloadDto | null
  {
    this.unlockLocally();
    return this.payload;
  }

  public onChange( listener: EventPayloadListener<DailyTasksPayloadDto> ): () => void
  {
    return this.listeners.add( listener );
  }

  private unlockLocally(): void
  {
    if ( applyLocalEventUnlock( this.payload ) )
    {
      console.info( "[DailyTasks] unlocked locally by level" );
      this.persist();
    }
  }

  public fetchedAgoMs(): number
  {
    return this.fetched_at > 0 ? Date.now() - this.fetched_at : Number.POSITIVE_INFINITY;
  }

  public async fetch( force = false ): Promise<DailyTasksPayloadDto | null>
  {
    const age = Date.now() - this.fetched_at;
    const max_age = this.isPeriodOver() ? EXPIRED_RETRY_MS : CACHE_TTL_MS;
    this.unlockLocally();
    if ( !force && this.payload && age < max_age )
      return this.payload;

    if ( !NetworkManager.isOnline() )
      return this.payload;

    if ( this.pending_fetch ) return this.pending_fetch;

    this.pending_fetch = this.fetchInternal();
    try
    {
      return await this.pending_fetch;
    }
    finally
    {
      this.pending_fetch = null;
    }
  }

  private async fetchInternal(): Promise<DailyTasksPayloadDto | null>
  {
    try
    {
      const res = await getDailyTasksConditional( this.etag );
      this.fetched_at = Date.now();
      if ( res.status === 304 )
        return this.payload;

      const payload = res.data;
      const changed = !this.payload || eventPayloadKey( this.payload ) !== eventPayloadKey( payload );
      this.payload = payload;
      this.etag = res.etag;
      this.persist();
      DailyTasksAnalytics.observe( payload );

      // The server pays out yesterday's earned-but-unclaimed rewards on this
      // fetch; balances arrive alongside so no extra sync round-trip is needed.
      if ( payload.auto_claimed?.length )
      {
        this.pending_auto_claimed.push( ...payload.auto_claimed );
        if ( payload.balances ) this.applyBalances( payload.balances );
        DailyTasksAnalytics.autoClaimed( payload, payload.auto_claimed );
      }
      if ( changed )
        this.listeners.notify( payload );
      return payload;
    }
    catch ( e )
    {
      console.warn( "Daily tasks fetch failed:", e );
      return this.payload;
    }
  }

  public getClaimableCount(): number
  {
    const payload = this.payload;
    if ( !payload?.enabled ) return 0;

    const tasks = payload.tasks.filter( task => task.completed && !task.claimed && this.isTaskDayOpen( task ) ).length;
    const chests = payload.stars?.chests.filter( chest => chest.claimable && !chest.claimed ).length ?? 0;
    return tasks + chests;
  }

  /** 1-based day of the weekly cycle the player is on. */
  public currentDay(): number
  {
    const payload = this.payload;
    const week_start_ms = Date.parse( payload?.stars?.week_start ?? "" );
    const period_ms = Date.parse( payload?.period_end ?? "" ) - Date.parse( payload?.period_start ?? "" );
    const elapsed_index = Number.isFinite( week_start_ms ) && period_ms > 0
      ? Math.floor( ( ServerClock.nowMs() - week_start_ms ) / period_ms )
      : 0;
    return Math.min( 7, Math.max( 1, Math.max( payload?.day_index ?? 0, elapsed_index ) + 1 ) );
  }

  /**
   * Tasks of later days are visible and progress already counts toward them,
   * but their rewards open only on their own day.
   */
  public isTaskDayOpen( task: DailyTaskDto ): boolean
  {
    return ( task.day ?? 0 ) <= this.currentDay();
  }

  /** Auto-granted rewards not yet shown to the player; consuming clears them. */
  public consumeAutoClaimed(): AutoClaimedTaskDto[]
  {
    const pending = this.pending_auto_claimed;
    this.pending_auto_claimed = [];
    return pending;
  }

  public applyLocalProgress( events: LocalMetricEvent[] ): void
  {
    this.unlockLocally();
    const payload = this.payload;
    if ( !payload?.enabled ) return;

    const now_ms = ServerClock.nowMs();
    let changed = false;
    for ( const event of events )
    {
      if ( SERVER_ONLY_METRICS.has( event.metric ) ) continue;
      for ( const task of payload.tasks )
      {
        if ( task.metric !== event.metric || task.claimed ) continue;
        if ( task.param && task.param !== ( event.param ?? null ) ) continue;
        const expires_ms = Date.parse( task.expires_at );
        if ( Number.isFinite( expires_ms ) && expires_ms <= now_ms ) continue;

        const progress = MAX_MODE_METRICS.has( event.metric )
          ? Math.max( task.progress, event.amount )
          : task.progress + event.amount;
        if ( progress === task.progress ) continue;

        task.progress = Math.min( progress, task.target );
        task.completed = task.progress >= task.target;
        changed = true;
      }
    }
    if ( changed )
    {
      this.persist();
      DailyTasksAnalytics.observe( payload );
    }
  }

  public findTask( task_id: string, day?: number ): DailyTaskDto | undefined
  {
    return this.payload?.tasks.find( entry => entry.id === task_id && ( day === undefined || entry.day === day ) );
  }

  public async claim( task_id: string, day?: number ): Promise<boolean>
  {
    const pending = this.findTask( task_id, day );
    if ( pending && !this.isTaskDayOpen( pending ) )
      return false;

    if ( shouldGoLocal() )
      return this.claimLocally( task_id, day );

    try
    {
      const response = await claimDailyTask( task_id, day );
      this.applyBalances( {
        coins: response.coins,
        hearts: response.hearts,
        infinite_hearts_until: response.infinite_hearts_until,
        no_ads_until: response.no_ads_until,
        boosters: response.boosters,
      } );

      const task = this.findTask( task_id, day );
      if ( task ) task.claimed = true;
      if ( response.stars && this.payload ) this.payload.stars = response.stars;
      this.persist();
      DailyTasksAnalytics.claimed( this.payload, task_id, response.rewards ?? [], day );
      return true;
    }
    catch ( e )
    {
      if ( isNetworkishError( e ) )
        return this.claimLocally( task_id, day );
      console.warn( `Daily task claim failed (${task_id}):`, e );
      // The screen re-fetches so a 409 (already claimed / not completed)
      // converges to the server's view instead of a stuck button.
      void this.fetch( true );
      return false;
    }
  }

  private claimLocally( task_id: string, day?: number ): boolean
  {
    const payload = this.payload;
    const task = this.findTask( task_id, day );
    if ( !payload || !task || task.claimed || !task.completed || !this.isTaskDayOpen( task ) )
      return false;
    const expires_ms = Date.parse( task.expires_at );
    if ( Number.isFinite( expires_ms ) && expires_ms <= ServerClock.nowMs() )
      return false;

    task.claimed = true;
    OfflineEconomy.grantRewards( task.rewards );
    if ( payload.stars )
    {
      payload.stars.current += Math.max( 0, Number( task.stars ) || 0 );
      for ( const chest of payload.stars.chests )
        chest.claimable = !chest.claimed && payload.stars.current >= chest.stars;
    }
    OfflineJournal.append( "daily_task_claim", { task_id, day: task.day ?? null, period_start: payload.period_start ?? null } );
    this.persist();
    DailyTasksAnalytics.claimed( payload, task_id, task.rewards, task.day );
    return true;
  }

  /** Resolves to the granted rewards on success, or null if the claim failed. */
  public async claimChest( index: number ): Promise<DailyTaskRewardDto[] | null>
  {
    if ( shouldGoLocal() )
      return this.claimChestLocally( index );

    try
    {
      const response = await claimStarChest( index );
      this.applyBalances( {
        coins: response.coins,
        hearts: response.hearts,
        infinite_hearts_until: response.infinite_hearts_until,
        no_ads_until: response.no_ads_until,
        boosters: response.boosters,
      } );
      if ( this.payload ) this.payload.stars = response.stars;
      this.persist();
      DailyTasksAnalytics.chestClaimed( this.payload, index, response.rewards );
      return response.rewards;
    }
    catch ( e )
    {
      if ( isNetworkishError( e ) )
        return this.claimChestLocally( index );
      console.warn( `Star chest claim failed (${index}):`, e );
      void this.fetch( true );
      return null;
    }
  }

  private claimChestLocally( index: number ): DailyTaskRewardDto[] | null
  {
    const payload = this.payload;
    const chest = payload?.stars?.chests[index];
    if ( !payload || !chest || chest.claimed || !chest.claimable )
      return null;

    chest.claimed = true;
    chest.claimable = false;
    OfflineEconomy.grantRewards( chest.rewards );
    OfflineJournal.append( "star_chest_claim", { index, week_start: payload.stars?.week_start ?? null } );
    this.persist();
    DailyTasksAnalytics.chestClaimed( payload, index, chest.rewards );
    return chest.rewards;
  }

  private applyBalances( balances: DailyTasksBalancesDto ): void
  {
    const stats = StatisticsStorage.load();
    stats.coins = balances.coins;
    stats.hearts = Math.max( stats.hearts, balances.hearts );
    stats.infinite_hearts_until = Math.max(
      stats.infinite_hearts_until,
      infiniteHeartsUntilMs( balances.infinite_hearts_until )
    );
    stats.no_ads_until = Math.max( stats.no_ads_until, serverUntilMs( balances.no_ads_until ) );
    StatisticsStorage.save( stats );
    CumulativeBoosterService.applyInventory( balances.boosters );
  }

  private persist(): void
  {
    if ( !this.payload ) return;
    const stored: PersistedDailyTasks = { payload: this.payload, fetched_at: this.fetched_at, etag: this.etag };
    PersistentCache.write( PERSIST_KEY, stored );
  }

  private isPeriodOver(): boolean
  {
    const end = this.payload?.period_end;
    if ( !end ) return false;
    const end_ms = Date.parse( end );
    return Number.isFinite( end_ms ) && ServerClock.nowMs() >= end_ms;
  }
}

export type { DailyTaskDto };
export const DailyTasksService = new DailyTasksServiceImpl();
